// Converts the shared @bbpos/ui TimeRangeValue into concrete ISO dates used
// by the reports API. Reporting pages default to "Current Month" per
// docs/sdlc/phase5/PAGES.md §7429.

import type { TimeRangeValue } from '@bbpos/ui';
import { calendarDay, todayIn } from '@/lib/store-clock';
import type { IsoDate, ReportDateRange } from './types';

function toIsoDate(d: Date): IsoDate {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

// Indian financial year (IBR-014): April 1 to March 31. Quarters are FY
// quarters — Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar.
function fyQuarterStart(d: Date): Date {
  const monthsSinceApril = (d.getMonth() + 9) % 12;
  return new Date(d.getFullYear(), d.getMonth() - (monthsSinceApril % 3), 1);
}

function fyStart(d: Date): Date {
  return new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1);
}

export const DEFAULT_REPORT_PRESET: TimeRangeValue = { preset: 'thisMonth' };

// With `timeZone`, "today" is that store's day (T20) — the zone the API buckets
// the report by — not this browser's.
export function resolveTimeRange(
  value: TimeRangeValue,
  timeZone?: string,
  now: Date = new Date(),
): ReportDateRange {
  const today = timeZone ? calendarDay(todayIn(timeZone, now)) : startOfDay(now);
  if ('from' in value && 'to' in value) {
    return { from: toIsoDate(value.from), to: toIsoDate(value.to) };
  }
  switch (value.preset) {
    case 'today':
      return { from: toIsoDate(today), to: toIsoDate(today) };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { from: toIsoDate(y), to: toIsoDate(y) };
    }
    case 'last7':
      return { from: toIsoDate(addDays(today, -6)), to: toIsoDate(today) };
    case 'last30':
      return { from: toIsoDate(addDays(today, -29)), to: toIsoDate(today) };
    case 'lastMonth': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: toIsoDate(first), to: toIsoDate(last) };
    }
    case 'thisQuarter':
      return { from: toIsoDate(fyQuarterStart(today)), to: toIsoDate(today) };
    case 'lastQuarter': {
      const start = fyQuarterStart(today);
      const first = new Date(start.getFullYear(), start.getMonth() - 3, 1);
      return { from: toIsoDate(first), to: toIsoDate(addDays(start, -1)) };
    }
    case 'thisFinancialYear':
      return { from: toIsoDate(fyStart(today)), to: toIsoDate(today) };
    case 'lastFinancialYear': {
      const start = fyStart(today);
      const first = new Date(start.getFullYear() - 1, 3, 1);
      return { from: toIsoDate(first), to: toIsoDate(addDays(start, -1)) };
    }
    case 'thisMonth':
    default: {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toIsoDate(first), to: toIsoDate(today) };
    }
  }
}

export function formatDateRangeLabel(range: ReportDateRange): string {
  return range.from === range.to ? range.from : `${range.from} → ${range.to}`;
}
